if (!customElements.get('user-story-gallery')) {
  /** Exponential decay constant for the touch fling, in ms. */
  const FLING_DECAY = 325;
  /** Slowest fling worth animating, in px/ms. */
  const FLING_MIN_VELOCITY = 0.05;
  /** Fling ceiling, in px/ms, so a stray sample cannot launch the lane. */
  const FLING_MAX_VELOCITY = 3;
  /** Duration of one nav-button step, in ms. */
  const NAV_STEP_DURATION = 420;

  class UserStoryGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.user-story-gallery__viewport');
      this.track = this.querySelector('[data-story-track]');
      this.lane = this.querySelector('[data-story-lane]');
      if (!this.track || !this.lane) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.baseSpeed = Number(this.dataset.scrollSpeed) || 56;
      this.hoverSpeed = Number(this.dataset.scrollSpeedHover) || 18;
      this.currentSpeed = this.baseSpeed;
      this.position = 0;
      this.flingVelocity = 0;
      this.lastTime = 0;
      this.loopWidth = 0;
      this.hasCloned = false;
      this.originalItems = [];
      this.isHovering = false;
      this.hoveringVideo = false;
      this.isInView = true;
      this.isDocumentVisible = document.visibilityState === 'visible';
      this.clickPlaying = null;
      this.hoverTarget = null;
      this.hoverTimer = 0;
      this.navTween = 0;
      this.isNavScrolling = false;
      this.isStatic = false;
      this.nav = this.querySelector('.user-story-gallery__nav');
      this.drag = {
        isPointerDown: false,
        moved: false,
        startX: 0,
        startPosition: 0,
        lastX: 0,
        lastTime: 0,
        velocity: 0,
        withMomentum: false,
      };

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionQuery.addEventListener('change', this.onMotionPreferenceChange, { signal });

      this.applyMotionMode();
      this.setupLoop();
      this.measureLoop();
      requestAnimationFrame(() => this.measureLoop());

      this.resizeObserver = new ResizeObserver(() => this.measureLoop());
      this.resizeObserver.observe(this.track);

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries.find((candidate) => candidate.target === this);
          this.isInView = Boolean(entry?.isIntersecting);
          this.updateAnimatingState();
        },
        { threshold: 0.12 },
      );
      this.intersectionObserver.observe(this);

      this.track.addEventListener('pointerdown', this.onPointerDown, { signal });
      this.track.addEventListener('pointermove', this.onPointerMove, { signal });
      this.track.addEventListener('pointerup', this.onPointerUp, { signal });
      this.track.addEventListener('pointercancel', this.onPointerUp, { signal });
      this.track.addEventListener('pointerleave', this.onPointerUp, { signal });
      this.track.addEventListener('scroll', this.onTrackScroll, { signal, passive: true });
      this.viewport?.addEventListener('pointerenter', this.onViewportEnter, { signal });
      this.viewport?.addEventListener('pointerleave', this.onViewportLeave, { signal });
      this.addEventListener('mouseover', this.onMouseOver, { signal });
      this.addEventListener('mouseout', this.onMouseOut, { signal });
      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('ended', this.onVideoEnded, { capture: true, signal });
      this.nav?.addEventListener('click', this.onNavClick, { signal });
      document.addEventListener('visibilitychange', this.onVisibilityChange, { signal });
      this.bindVideos();
      this.updateAnimatingState();

      this.raf = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      cancelAnimationFrame(this.raf);
      this.cancelNavTween();
      this.clearHoverTimer();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.stopAllVideos();
      this.lane?.classList.remove('is-animating');
    }

    /**
     * Duplicates the original cards so the marquee can loop without a jump.
     */
    setupLoop() {
      this.lane.querySelectorAll('[data-story-clone]').forEach((node) => node.remove());
      this.hasCloned = false;
      this.originalItems = Array.from(this.lane.children).filter(
        (node) => !node.hasAttribute('data-story-clone'),
      );
      if (this.prefersReducedMotion() || this.originalItems.length < 2) return;

      this.originalItems.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-story-clone', '');
        clone.querySelectorAll('[data-story-play]').forEach((button) => {
          button.setAttribute('tabindex', '-1');
        });
        this.prepareCloneMedia(clone);
        this.lane.appendChild(clone);
        this.mountCloneMedia(clone);
      });
      this.hasCloned = true;
    }

    /**
     * Cloned picture-breakpoint nodes copy in-flight lazy state and then
     * refuse to load, which shows empty cards when the loop wraps.
     * @param {HTMLElement} clone
     */
    prepareCloneMedia(clone) {
      clone.querySelectorAll('.is-playing').forEach((card) => {
        card.classList.remove('is-playing');
      });
      clone.querySelectorAll('picture-breakpoint').forEach((element) => {
        delete element.dataset.mounted;
        delete element.dataset.loading;
      });
      clone.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
        video.muted = true;
      });
    }

    /**
     * @param {HTMLElement} clone
     */
    mountCloneMedia(clone) {
      clone.querySelectorAll('picture-breakpoint').forEach((element) => {
        if (typeof element.loadImage === 'function') element.loadImage();
      });
      this.bindVideos(clone);
    }

    measureLoop() {
      if (!this.originalItems.length) return;

      const first = this.originalItems[0];
      const firstClone = this.hasCloned ? this.lane.querySelector('[data-story-clone]') : null;

      if (first && firstClone) {
        // Rects rather than offsetLeft: offsetLeft rounds to whole pixels, and a
        // rounded loop width shows up as a jump every time the marquee wraps.
        this.loopWidth = firstClone.getBoundingClientRect().left - first.getBoundingClientRect().left;
        return;
      }

      const styles = getComputedStyle(this.lane);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const itemsWidth = this.originalItems.reduce(
        (sum, element) => sum + element.getBoundingClientRect().width,
        0,
      );
      this.loopWidth = itemsWidth + gap * Math.max(this.originalItems.length - 1, 0);
    }

    /**
     * Reduced motion keeps the native scroller instead of the transform loop.
     */
    applyMotionMode() {
      this.isStatic = this.prefersReducedMotion();
      this.track.classList.toggle('is-static', this.isStatic);
      if (this.isStatic) {
        this.lane.style.transform = '';
        this.lane.classList.remove('is-animating');
      }
    }

    updateAnimatingState() {
      this.lane.classList.toggle(
        'is-animating',
        !this.isStatic && this.isInView && this.isDocumentVisible,
      );
    }

    /**
     * Writes the loop position as a transform, which — unlike scrollLeft — is
     * not snapped to whole device pixels.
     * @param {number} value
     */
    setPosition(value) {
      this.position = value;
      this.wrapPosition();
      if (this.isStatic) return;
      this.lane.style.transform = `translate3d(${-this.position}px, 0, 0)`;
    }

    /**
     * @param {number} value
     * @returns {number}
     */
    wrapValue(value) {
      if (this.loopWidth <= 1) return value;
      let wrapped = value;
      while (wrapped >= this.loopWidth) {
        wrapped -= this.loopWidth;
      }
      while (wrapped < 0) {
        wrapped += this.loopWidth;
      }
      return wrapped;
    }

    wrapPosition() {
      this.position = this.wrapValue(this.position);
    }

    /**
     * @returns {boolean}
     */
    prefersReducedMotion() {
      return this.reducedMotionQuery?.matches;
    }

    /**
     * @returns {boolean}
     */
    canAutoScroll() {
      if (!this.track || this.prefersReducedMotion()) return false;
      if (!this.isInView || !this.isDocumentVisible) return false;
      if (this.drag.isPointerDown || this.isNavScrolling) return false;
      return this.loopWidth > 0;
    }

    /**
     * @returns {number}
     */
    getTargetSpeed() {
      if (this.hoveringVideo || this.clickPlaying) return 0;
      return this.baseSpeed;
    }

    tick = (now) => {
      if (!this.lastTime) this.lastTime = now;
      const delta = Math.min(32, now - this.lastTime);
      this.lastTime = now;

      if (this.isStatic) {
        this.raf = requestAnimationFrame(this.tick);
        return;
      }

      if (this.flingVelocity) {
        this.advanceFling(delta);
      } else if (this.canAutoScroll()) {
        const targetSpeed = this.getTargetSpeed();
        const easing = 1 - Math.exp(-delta / 160);
        this.currentSpeed += (targetSpeed - this.currentSpeed) * easing;
        if (Math.abs(this.currentSpeed) < 0.05 && targetSpeed === 0) {
          this.currentSpeed = 0;
        } else {
          this.setPosition(this.position + (this.currentSpeed * delta) / 1000);
        }
      }

      this.raf = requestAnimationFrame(this.tick);
    };

    /**
     * Decays the post-swipe glide and hands the leftover speed to the marquee,
     * so the lane never drops from gliding to auto-scroll in one frame.
     * @param {number} delta ms since the previous frame
     */
    advanceFling(delta) {
      this.setPosition(this.position + this.flingVelocity * delta);
      this.flingVelocity *= Math.exp(-delta / FLING_DECAY);

      const speedPerSecond = this.flingVelocity * 1000;
      if (Math.abs(speedPerSecond) <= this.baseSpeed) {
        this.currentSpeed = speedPerSecond;
        this.flingVelocity = 0;
      }
    }

    /**
     * @returns {number}
     */
    getCardStep() {
      const first = this.originalItems[0];
      if (!first) return 0;
      const styles = getComputedStyle(this.lane);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    /**
     * @param {number} direction
     */
    scrollByCard(direction) {
      const step = this.getCardStep();
      if (!this.track || step <= 0) return;

      if (this.isStatic) {
        this.track.scrollTo({
          left: this.wrapValue(this.track.scrollLeft + direction * step),
          behavior: 'smooth',
        });
        return;
      }

      this.cancelNavTween();
      this.flingVelocity = 0;
      this.isNavScrolling = true;

      const from = this.position;
      const distance = direction * step;
      const startedAt = performance.now();

      const frame = (now) => {
        const progress = Math.min(1, (now - startedAt) / NAV_STEP_DURATION);
        // easeOutCubic keeps the soft arrival of the smooth scroll it replaces.
        this.setPosition(from + distance * (1 - (1 - progress) ** 3));

        if (progress < 1) {
          this.navTween = requestAnimationFrame(frame);
          return;
        }
        this.navTween = 0;
        this.isNavScrolling = false;
      };

      this.navTween = requestAnimationFrame(frame);
    }

    cancelNavTween() {
      if (this.navTween) cancelAnimationFrame(this.navTween);
      this.navTween = 0;
      this.isNavScrolling = false;
    }

    onNavClick = (event) => {
      const button = event.target.closest('.user-story-gallery__nav-btn');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      this.scrollByCard(button.classList.contains('user-story-gallery__nav-btn--next') ? 1 : -1);
    };

    onMotionPreferenceChange = (event) => {
      if (event.matches) {
        this.stopAllVideos();
      }
      this.applyMotionMode();
      this.updateAnimatingState();
    };

    onVisibilityChange = () => {
      this.isDocumentVisible = document.visibilityState === 'visible';
      if (!this.isDocumentVisible) {
        this.pauseHoverVideo();
      }
      this.updateAnimatingState();
    };

    /**
     * Focusing an off-screen card scrolls the clipped track and desyncs it from
     * the transform, so the scroller is pinned back to the start.
     */
    onTrackScroll = () => {
      if (this.isStatic || !this.track.scrollLeft) return;
      this.track.scrollLeft = 0;
    };

    onViewportEnter = () => {
      this.isHovering = true;
    };

    onViewportLeave = () => {
      this.isHovering = false;
      this.hoveringVideo = false;
      this.clearHoverTimer();
      this.pauseHoverVideo();
      this.hoverTarget = null;
    };

    onMouseOver = (event) => {
      const card = event.target.closest('.user-story-gallery__card');
      if (!card || !this.contains(card) || this.hoverTarget === card) return;

      this.clearHoverTimer();
      this.pauseHoverVideo();
      this.hoverTarget = card;
      this.hoveringVideo = card.hasAttribute('data-has-video');

      if (!this.hoveringVideo || this.clickPlaying) return;

      this.hoverTimer = window.setTimeout(() => {
        if (this.clickPlaying || this.hoverTarget !== card) return;
        this.playVideo(card, { muted: true, pauseScroll: false });
      }, 200);
    };

    onMouseOut = (event) => {
      const card = event.target.closest('.user-story-gallery__card');
      if (!card || this.hoverTarget !== card) return;

      const related = event.relatedTarget;
      if (related && card.contains(related)) return;

      this.clearHoverTimer();
      this.pauseHoverVideo();
      this.hoverTarget = null;
      this.hoveringVideo = false;
    };

    onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.cancelNavTween();
      this.flingVelocity = 0;

      this.drag.isPointerDown = true;
      this.drag.moved = false;
      this.drag.startX = event.clientX;
      this.drag.startPosition = this.isStatic ? this.track.scrollLeft : this.position;
      this.drag.lastX = event.clientX;
      this.drag.lastTime = event.timeStamp;
      this.drag.velocity = 0;
      // Mouse drags keep their existing stop-on-release feel; touch and pen glide.
      this.drag.withMomentum = !this.isStatic && event.pointerType !== 'mouse';
      this.track.classList.add('is-dragging');
    };

    onPointerMove = (event) => {
      if (!this.drag.isPointerDown) return;

      const travel = event.clientX - this.drag.startX;
      if (Math.abs(travel) > 4) this.drag.moved = true;

      const elapsed = event.timeStamp - this.drag.lastTime;
      if (elapsed > 0) {
        // Smoothed so one noisy sample cannot decide the fling speed.
        const sample = (event.clientX - this.drag.lastX) / elapsed;
        this.drag.velocity = this.drag.velocity * 0.7 + sample * 0.3;
        this.drag.lastX = event.clientX;
        this.drag.lastTime = event.timeStamp;
      }

      if (this.isStatic) {
        this.track.scrollLeft = this.drag.startPosition - travel;
        return;
      }
      this.setPosition(this.drag.startPosition - travel);
    };

    onPointerUp = () => {
      if (!this.drag.isPointerDown) return;

      this.drag.isPointerDown = false;
      this.track?.classList.remove('is-dragging');

      if (this.drag.withMomentum && Math.abs(this.drag.velocity) > FLING_MIN_VELOCITY) {
        // A finger moving left drags the content left, so position grows.
        const velocity = Math.max(-FLING_MAX_VELOCITY, Math.min(FLING_MAX_VELOCITY, this.drag.velocity));
        this.flingVelocity = -velocity;
      }
      this.drag.withMomentum = false;
    };

    onClick = (event) => {
      if (event.target.closest('.user-story-gallery__nav')) return;

      if (this.drag.moved) {
        event.preventDefault();
        this.drag.moved = false;
        return;
      }

      const card = event.target.closest('[data-has-video]');
      if (!card || !this.contains(card)) return;

      event.preventDefault();

      if (this.clickPlaying === card) {
        this.stopVideo(card);
        return;
      }

      this.playVideo(card, { muted: false, pauseScroll: true });
    };

    onVideoEnded = (event) => {
      const video = event.target;
      if (!video.matches?.('[data-story-video]')) return;

      const card = video.closest('[data-has-video]');
      if (!card) return;

      this.stopVideo(card);
    };

    /**
     * Plays a story video inside its card.
     * @param {HTMLElement} card
     * @param {{ muted: boolean, pauseScroll: boolean }} options
     */
    playVideo(card, { muted, pauseScroll }) {
      if (!card) return;

      this.stopAllVideos({ except: card });
      card.classList.add('is-playing');

      if (pauseScroll) {
        this.clickPlaying = card;
      }

      const video = card.querySelector('[data-story-video]');
      if (video) {
        this.fitVideo(video);
        video.muted = muted;
        if (!muted) video.volume = 1;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            if (muted) return;
            video.muted = true;
            video.play().catch(() => {});
          });
        }
        return;
      }

      this.playYoutube(card, muted);
    }

    /**
     * @param {HTMLElement} card
     * @param {boolean} muted
     */
    playYoutube(card, muted) {
      const youtubeId = this.getYoutubeId(card.dataset.videoUrl || '');
      if (!youtubeId) return;

      const media = card.querySelector('.user-story-gallery__media');
      if (!media) return;

      let iframe = media.querySelector('iframe.user-story-gallery__video');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.className = 'user-story-gallery__video';
        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.title = 'Customer story video';
        media.appendChild(iframe);
      }

      iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=${muted ? 1 : 0}&playsinline=1&rel=0`;
    }

    /**
     * Stops in-card playback and returns the poster image.
     * @param {HTMLElement} card
     */
    stopVideo(card) {
      if (!card) return;

      const video = card.querySelector('[data-story-video]');
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.muted = true;
      }

      const iframe = card.querySelector('iframe.user-story-gallery__video');
      if (iframe) iframe.src = '';

      card.classList.remove('is-playing');

      if (this.clickPlaying === card) {
        this.clickPlaying = null;
      }
    }

    /**
     * @param {{ except?: HTMLElement }} [options]
     */
    stopAllVideos({ except } = {}) {
      this.querySelectorAll('[data-has-video].is-playing').forEach((card) => {
        if (card === except) return;
        this.stopVideo(card);
      });
    }

    pauseHoverVideo() {
      if (this.clickPlaying) return;
      this.querySelectorAll('[data-has-video].is-playing').forEach((card) => {
        this.stopVideo(card);
      });
    }

    clearHoverTimer() {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = 0;
    }

    /**
     * Scales a file video so it covers the card on iOS, where object-fit is ignored.
     * @param {HTMLVideoElement} video
     */
    fitVideo(video) {
      if (!video || video.tagName !== 'VIDEO') return;

      const apply = () => {
        const media = video.closest('.user-story-gallery__media');
        if (!media || !video.videoWidth || !video.videoHeight) return;
        const scale = Math.max(
          media.clientWidth / video.videoWidth,
          media.clientHeight / video.videoHeight,
        );
        video.style.width = `${Math.ceil(video.videoWidth * scale)}px`;
        video.style.height = `${Math.ceil(video.videoHeight * scale)}px`;
      };

      if (video.readyState >= 1) {
        apply();
        return;
      }
      video.addEventListener('loadedmetadata', apply, { once: true });
    }

    /**
     * @param {ParentNode} [root]
     */
    bindVideos(root = this) {
      root.querySelectorAll('[data-story-video]').forEach((video) => {
        this.fitVideo(video);
      });
    }

    getYoutubeId(url) {
      const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
      );
      return match ? match[1] : '';
    }
  }

  customElements.define('user-story-gallery', UserStoryGallery);
}
